/**
 * Welcome / Home — search-first entry (doc 147 §5.2).
 *
 * The global constellation is opt-in (Phase 2); Phase 1's welcome surfaces the
 * hero stats, recents, and a domain grid.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import { Icon } from '../components/Icon.js';
import { EmptyState } from '../components/EmptyState.js';
import { domainIcon } from '../icons/catalog.js';
import { domainsSorted, manifestSummary, entityById } from '../state/selectors.js';
import { navigate, navigateToEntity } from '../state/hashRouter.js';

export interface WelcomeViewProps {
  manifest: AtlasManifest;
  recents: string[];
  onOpenSearch: () => void;
}

export function WelcomeView({
  manifest,
  recents,
  onOpenSearch,
}: WelcomeViewProps): React.JSX.Element {
  const summary = manifestSummary(manifest);
  const domains = domainsSorted(manifest);
  const recentEntities = recents
    .map((id) => entityById(manifest, id))
    .filter((e): e is NonNullable<typeof e> => e !== undefined)
    .slice(0, 6);

  return (
    <div>
      <section className="hero">
        <div>
          <h1 className="hero-title">Navigate the model</h1>
          <p className="editorial" style={{ color: 'var(--brand-subtle)', fontSize: 'var(--fs-md)' }}>
            before you build it.
          </p>
          <p style={{ color: 'var(--text2)', margin: 'var(--sp-sm) 0 var(--sp-md)' }}>
            {summary.entityCount} entities across {summary.domainCount} domains. Click anything,
            see how it connects.
          </p>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
            <button className="btn btn-primary" onClick={onOpenSearch}>
              <Icon name="search" size="sm" />
              Search the model
            </button>
            <button
              className="btn btn-ghost"
              onClick={() =>
                navigate({ kind: 'workspace', mode: { kind: 'idle' } })
              }
            >
              <Icon name="scatter_plot" size="sm" />
              Open workspace
            </button>
          </div>
        </div>
        <div className="hero-stats">
          <div>
            <div className="hero-stat-num" style={{ color: 'var(--good)' }}>
              {summary.entityCount}
            </div>
            <div className="hero-stat-label">ENTITIES</div>
          </div>
          <div>
            <div className="hero-stat-num" style={{ color: 'var(--info)' }}>
              {summary.domainCount}
            </div>
            <div className="hero-stat-label">DOMAINS</div>
          </div>
          <div>
            <div className="hero-stat-num" style={{ color: 'var(--brand)' }}>
              {summary.surfaceCount}
            </div>
            <div className="hero-stat-label">SURFACES</div>
          </div>
          <div>
            <div className="hero-stat-num" style={{ color: 'var(--caution)' }}>
              {summary.referenceCount}
            </div>
            <div className="hero-stat-label">REFERENCES</div>
          </div>
        </div>
      </section>

      {recentEntities.length > 0 && (
        <section style={{ marginBottom: 'var(--sp-xl)' }}>
          <div className="section-label">Recently viewed</div>
          <div className="domain-grid">
            {recentEntities.map((entity) => (
              <button
                key={entity.id}
                className="domain-card"
                onClick={() => navigateToEntity(entity.id)}
              >
                <div className="domain-card-head">
                  <Icon name="description" size="sm" />
                  <span className="domain-card-name mono">{entity.name}</span>
                </div>
                <span className="domain-card-meta">{entity.domain}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="section-label">Explore by domain</div>
        {domains.length === 0 ? (
          <EmptyState icon="hub" title="No domains in the manifest yet" />
        ) : (
          <div className="domain-grid">
            {domains.map((domain) => (
              <button
                key={domain.id}
                className="domain-card"
                onClick={() => navigate({ kind: 'domain', id: domain.id })}
              >
                <div className="domain-card-head">
                  <Icon
                    name={domainIcon(domain.id)}
                    size="md"
                    color={`var(--${domain.color.replace(/^--/, '')})`}
                  />
                  <span className="domain-card-name">{domain.name}</span>
                </div>
                <span className="domain-card-meta">
                  {domain.entityCount} entities · {domain.docRef}
                </span>
                {domain.packGate && (
                  <span className="domain-card-meta">🔓 {domain.packGate} pack</span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
